import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import images from 'src/common/images';
import { ErrorMstContractPeriod, MstContractPeriod, MutationSet } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteMstContractPeriod, insertMstContractPeriod, updateMstContractPeriod } from 'src/graphql/mutations';
import { getMstContractPeriod } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import MainLayout from '../main-layout';

const defaultErrorMstContractPeriod = {
  isErrorYears: false,
  isErrorMaintenanceDiscountRate: false,
  isErrorUsageFeeDiscountRate: false,
};

const emptyContractPeriod = {
  mcp_years: '',
  mcp_maintenance_discount_rate: '',
  mcp_usage_fee_discount_rate: '',
  recordState: RecordState.INSERT,
  isCannotEditYears: false,
  error: defaultErrorMstContractPeriod,
  isBeginData: false,
};

const ContractPeriodManage = () => {
  const header = ['年数', '保守割引率', '利用料割引率', '削除'];
  const [mstContractPeriod, setMstContractPeriod] = useState<MstContractPeriod[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const [removedMstContractPeriod, setRemovedMstContractPeriod] = useState<MstContractPeriod[]>([]);
  const [beginLengthListContractPeriod, setBeginLengthListContractPeriod] = useState<number>(0);
  const [updateFunc] = useAppMutation({ query: gql(updateMstContractPeriod) });
  const [deleteFunc] = useAppMutation({ query: gql(deleteMstContractPeriod) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstContractPeriod) });
  const [searchMstContractPeriod, { data: mstContractPeriodData, loading: isMstContractPeriodLoading }] =
    useAppLazyQuery({
      query: gql(getMstContractPeriod),
      onCompleted: () => {
        setRemovedMstContractPeriod([]);
      },
    });

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const betweenNumber = (val: number, min: number, max: number) => {
    return val >= min && val <= max;
  };

  const renderTableData = useMemo(() => {
    if (!mstContractPeriod || mstContractPeriod.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent w-full`;
    };
    return mstContractPeriod.map((contractPeriod: MstContractPeriod, index: number) => {
      const isLastItem = index === mstContractPeriod.length - 1;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] ">
            <input
              className={`${styleInput(contractPeriod.error.isErrorYears)} text-right`}
              type="number"
              onChange={(event) => {
                if (event.target.value.length === 3) return false;
                handleChangeContractPeriod(event.target.value, index, 'mcp_years');
              }}
              name="id"
              value={contractPeriod.mcp_years}
              disabled={contractPeriod.isCannotEditYears}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(contractPeriod.error.isErrorMaintenanceDiscountRate)}
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeContractPeriod(event.target.value, index, 'mcp_maintenance_discount_rate');
              }}
              type="number"
              name="maintenance"
              min={0}
              max={100}
              step="0.01"
              value={contractPeriod.mcp_maintenance_discount_rate}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(false)}
              onChange={(event) => {
                if (!betweenNumber(Number(event.target.value), 0, 100)) return;
                handleChangeContractPeriod(event.target.value, index, 'mcp_usage_fee_discount_rate');
              }}
              type="number"
              name="fee"
              min={0}
              max={100}
              step="0.01"
              value={contractPeriod.mcp_usage_fee_discount_rate}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] text-center">
            <button onClick={() => onPressConfirmRemove(index)}>
              {!isLastItem ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstContractPeriod]);

  const handleChangeContractPeriod = (value: string, index: number, typeContractPeriod: string) => {
    const newListMstContractPeriod = mstContractPeriod.slice();
    newListMstContractPeriod[index] = { ...newListMstContractPeriod[index], ...{ [typeContractPeriod]: value } };
    const errorObject: ErrorMstContractPeriod = { ...newListMstContractPeriod[index].error };

    switch (typeContractPeriod) {
      case 'mcp_years':
        errorObject.isErrorYears = false;
        break;
      case 'mcp_maintenance_discount_rate':
        errorObject.isErrorMaintenanceDiscountRate = false;
        break;
      case 'mcp_usage_fee_discount_rate':
        errorObject.isErrorUsageFeeDiscountRate = false;
        break;
    }
    newListMstContractPeriod[index].error = { ...newListMstContractPeriod[index].error, ...errorObject };
    if (newListMstContractPeriod[index].isBeginData) {
      newListMstContractPeriod[index].recordState = RecordState.UPDATE;
    }
    setMstContractPeriod(newListMstContractPeriod);
  };

  const isEmptyData = (data: MstContractPeriod) => {
    return (
      data.mcp_years === '' && data.mcp_maintenance_discount_rate === '' && data.mcp_usage_fee_discount_rate === ''
    );
  };

  const onPressRemove = (index: number) => {
    const newListContractPeriod = mstContractPeriod.slice();
    if (index < beginLengthListContractPeriod) {
      const newListRemove = [...removedMstContractPeriod];
      const dataItem = mstContractPeriod[index];
      setBeginLengthListContractPeriod(beginLengthListContractPeriod - 1);
      newListRemove.push({ ...dataItem, recordState: RecordState.DELETE });
      setRemovedMstContractPeriod(newListRemove);
    }
    newListContractPeriod.splice(index, 1);
    setMstContractPeriod(newListContractPeriod);
  };

  const prepareDataApplyApi = (contractPeriodData: MstContractPeriod) => {
    return {
      mcp_years: contractPeriodData.mcp_years,
      mcp_maintenance_discount_rate: contractPeriodData.mcp_maintenance_discount_rate,
      mcp_usage_fee_discount_rate: contractPeriodData.mcp_usage_fee_discount_rate,
    };
  };

  const onPressConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  const checkError = (itemContractPeriod: MstContractPeriod) => {
    const isErrorYears = itemContractPeriod.mcp_years === '';
    const isErrorMaintenanceDiscountRate = itemContractPeriod.mcp_maintenance_discount_rate === '';
    const isErrorUsageFeeDiscountRate = itemContractPeriod.mcp_usage_fee_discount_rate === '';
    return { isErrorYears, isErrorMaintenanceDiscountRate, isErrorUsageFeeDiscountRate };
  };

  const isExistItem = () => {
    const newListContractPeriod = mstContractPeriod.slice();
    const valueArr = newListContractPeriod.map((item) => {
      return item.mcp_years.toString();
    });
    const isDuplicate = valueArr.some((item, idx) => {
      if (valueArr.indexOf(item) !== idx) {
        newListContractPeriod[idx].error.isErrorYears = true;
        return true;
      }
      return false;
    });
    setMstContractPeriod(newListContractPeriod);
    return isDuplicate;
  };

  const validates = () => {
    const newListContractPeriod = mstContractPeriod.slice();
    newListContractPeriod.pop();
    const newListAddError = newListContractPeriod.map((itemContractPeriod: MstContractPeriod) => {
      return { ...itemContractPeriod, error: checkError(itemContractPeriod) };
    });
    setMstContractPeriod(newListAddError);
    const indexItemError = newListContractPeriod.findIndex(
      (itemContractPeriod: MstContractPeriod) =>
        itemContractPeriod.mcp_years === '' ||
        itemContractPeriod.mcp_maintenance_discount_rate === '' ||
        itemContractPeriod.mcp_usage_fee_discount_rate === ''
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const executeRegister = async () => {
    const mstOfficeNewList = mstContractPeriod.slice();
    const insertData = mstOfficeNewList.filter((item: MstContractPeriod) => item.recordState === RecordState.INSERT);
    const updateData = mstOfficeNewList.filter((item: MstContractPeriod) => item.recordState === RecordState.UPDATE);

    // 最終行(新規行)を除外
    insertData.pop();

    const delSet: MutationSet<MstContractPeriod> = {
      recordState: RecordState.DELETE,
      data: removedMstContractPeriod,
      func: (data: MstContractPeriod) => deleteFunc({ variables: { input: { mcp_years: data.mcp_years } } }),
    };
    const updSet: MutationSet<MstContractPeriod> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstContractPeriod) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };
    const insSet: MutationSet<MstContractPeriod> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstContractPeriod) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = () => searchMstContractPeriod({ variables: { input: {} } });

    registerData<MstContractPeriod>({
      mutationSets1: [delSet, updSet, insSet],
      validates,
      searchFunc,
    });
  };

  const onRegisterData = async () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, executeRegister)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  useEffect(() => {
    if (mstContractPeriod?.length < 1) return;
    const lastIndex = mstContractPeriod.length - 1;
    const newListMstContractPeriod = mstContractPeriod.slice();

    if (!isEmptyData(newListMstContractPeriod[lastIndex])) {
      newListMstContractPeriod.push(emptyContractPeriod);
      setMstContractPeriod(newListMstContractPeriod);
    }
  }, [mstContractPeriod]);

  useEffect(() => {
    if (!mstContractPeriodData) return;

    const newListContractPeriod = mstContractPeriodData.getMstContractPeriod.map((item: MstContractPeriod) => {
      return {
        ...item,
        recordState: RecordState.NONE,
        isCannotEditYears: true,
        error: defaultErrorMstContractPeriod,
        isBeginData: true,
      };
    });
    setBeginLengthListContractPeriod(mstContractPeriodData.getMstContractPeriod.length);
    newListContractPeriod.sort(
      (a: MstContractPeriod, b: MstContractPeriod) => Number(a.mcp_years) - Number(b.mcp_years)
    );
    newListContractPeriod.push(emptyContractPeriod);
    setMstContractPeriod(newListContractPeriod);
  }, [mstContractPeriodData]);

  useEffect(() => {
    searchMstContractPeriod({ variables: { input: {} } });
  }, []);

  return (
    <MainLayout>
      <div className="flex flex-col justify-center items-center">
        <p className="p-[20px]">契約年数管理</p>
        <div className="flex w-4/5">
          <button
            onClick={onRegisterData}
            className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] font-bold"
          >
            登録
          </button>
        </div>
        <table className="border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
          <thead>
            <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
          </thead>
          <tbody>
            {isMstContractPeriodLoading ? (
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                <td
                  colSpan={header.length}
                  className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                >
                  Loading...
                </td>
              </tr>
            ) : (
              renderTableData
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

export default ContractPeriodManage;
